import {
  Badge,
  Button,
  Flex,
  FormLabel,
  Heading,
  Input,
  InputAddon,
  InputGroup,
  Select,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import Paginator from '../../../components/paginator'
import { VideoQuizService } from '../../../services/VideoQuiz/VideoQuiz.service'
import { IVideoQuiz, IListFilters } from './models'
import { VideoQuizDeleteDialog } from './VideoQuizDeleteDialog'
import { VideoQuizViewModal } from './VideoQuizViewModal'

export default function VideoQuizPage() {
  const service = new VideoQuizService()
  const cancelRef = useRef()
  const toast = useToast()
  const [videoQuizes, setVideoQuizes] = useState<IVideoQuiz[]>([])
  const [length, setLength] = useState(25)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [draw, setDraw] = useState(0)
  const [search, setSearch] = useState('')
  const [modalId, setModalId] = useState<number>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const [deleteId, SetDeleteId] = useState<number>()
  const [filters, setFilters] = useState<IListFilters>({
    search: '',
    status: 'all',
    created_at: {
      from: '',
      to: ''
    }
  })

  const loadVideoQuizes = async () => {
    const leadsList = await service.list({
      draw,
      length,
      start: (page - 1) * length,
      order: [{ column: 0, dir: 'desc' }],
      filters
    })

    setDraw(draw + 1)

    setTotalPages(Math.ceil(leadsList.recordsFiltered / length))

    setVideoQuizes(leadsList.data)
  }

  useEffect(() => {
    loadVideoQuizes()
  }, [page, search, length, filters])

  const handleOpenModal = async (id: number) => {
    setModalId(id)
    setIsModalOpen(true)
  }

  const handleDeleteDialog = (id: number) => {
    deleteDialogOnOpen()
    SetDeleteId(id)
  }

  const calcPercentage = (value1: number, value2: number): string => {
    if (value1 === 0 && value2 === 0) return '0.00'

    return ((value1 / (value1 + value2)) * 100).toFixed(2)
  }

  return (
    <>
      <Heading as="h1" size="lg" marginBottom="5" mb="10" textTransform="uppercase">
        Video Quiz
      </Heading>
      <div className="row mb-3">
        <div className="col-2">
          <FormLabel htmlFor="status" fontSize="sm" fontWeight="normal">
            Status
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="status"
            onChange={(e) => setFilters({ ...filters, status: e.target.value as 'all' | 'completed' | 'in-progress' })}
            value={filters.status}
          >
            <option value="all">Todos</option>
            <option value="completed">Finalizado</option>
            <option value="in-progress">Em andamento</option>
          </Select>
        </div>
        <div className="col-4">
          <FormLabel htmlFor="date-start" fontSize="sm" fontWeight="normal">
            Data de criação
          </FormLabel>
          <InputGroup display="flex" alignItems="center" size="sm">
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-start"
              value={filters.created_at.from}
              onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, from: e.target.value } })}
              max={filters.created_at.to}
            />
            <InputAddon>Até</InputAddon>
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-end"
              value={filters.created_at.to}
              onChange={(e) => setFilters({ ...filters, created_at: { ...filters.created_at, to: e.target.value } })}
              min={filters.created_at.from}
            />
          </InputGroup>
        </div>
      </div>
      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setLength(parseInt(e.target.value))}
            value={length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        </Flex>
      </Flex>
      <TableContainer fontSize="smaller" padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th p={1}>ID</Th>
              <Th p={1}>Nome</Th>
              <Th p={1}>E-mail</Th>
              <Th p={1}>Telefone</Th>
              <Th p={1}>Progresso</Th>
              <Th p={1}>Nota</Th>
              <Th p={1}>Iniciado em</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {videoQuizes.length === 0 && (
              <Tr>
                <Td p={3} colSpan={8} textAlign="center">
                  Nenhum resultado encontrado
                </Td>
              </Tr>
            )}
            {videoQuizes.map((workSubmission) => (
              <Tr key={workSubmission.id}>
                <Td p={1}>{workSubmission.id}</Td>
                <Td p={1}>{workSubmission.name}</Td>
                <Td p={1}>{workSubmission.email}</Td>
                <Td p={1}>{workSubmission.phone}</Td>
                <Td p={1}>
                  {Math.round((workSubmission.current_step / 6) * 100)}%
                  {workSubmission.current_step === 6 ? (
                    <Badge colorScheme="green" ml="2" fontSize="0.8em">
                      Finalizado
                    </Badge>
                  ) : (
                    <Badge colorScheme="yellow" ml="2" fontSize="0.8em">
                      Em andamento
                    </Badge>
                  )}
                </Td>
                <Td p={1}>{workSubmission.correct_answers_count * 2}</Td>
                <Td p={1}>
                  {new Date(workSubmission.created_at).toLocaleString('pt-BR').replace(',', ' -').slice(0, -3)}
                </Td>
                <Td p={1}>
                  <Button
                    onClick={() => handleOpenModal(workSubmission.id)}
                    colorScheme="magenta"
                    variant="outline"
                    size="xs"
                    mb="2"
                    ml="0"
                    mr="0"
                  >
                    <i className="lni lni-eye"></i>
                  </Button>
                  <Button
                    onClick={() => handleDeleteDialog(workSubmission.id)}
                    colorScheme="magenta"
                    size="xs"
                    mb="2"
                    ml="2"
                    mr="0"
                  >
                    <i className="lni lni-trash-can"></i>
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      <VideoQuizViewModal id={modalId} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <VideoQuizDeleteDialog
        id={deleteId}
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        leastDestructiveRef={cancelRef}
        onDelete={() => {
          deleteDialogIsClose()
          loadVideoQuizes()
        }}
      />
      <Tooltip label="Copiar link para área de transferência" aria-label="Copy link" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            navigator.clipboard.writeText('https://abra.com.br/video-quiz')

            toast({ title: 'Link copiado!', status: 'success', duration: 2000, isClosable: false })
          }}
        >
          <i className="lni lni-link"></i>
        </Button>
      </Tooltip>
    </>
  )
}
